import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { AiOutlinePlus } from 'react-icons/ai'
import { BiQrScan } from 'react-icons/bi'
import { useNavigate } from 'react-router-dom'

import { userAssignmentCodes } from '../../api/services/user_assignment_codes'
import { UserCode } from '../../api/services/user_assignment_codes/types.ts'
import PopupAddCode from '../../components/assignmentCodes/PopupAddCode/PopupAddCode.tsx'
import Alert from '../../components/common/Alert/Alert.tsx'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Button from '../../components/common/Button/Button.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import Popup from '../../components/common/Popup/Popup.tsx'
import Table from '../../components/common/Table/Table.tsx'
import { useAuth } from '../../context/auth'
import useSubAccountDetails from '../../hooks/useSubAccountDetails.ts'
import {
  setAssignmentCodesTableActions,
  setAssignmentCodesTableColumns
} from '../../utils/assignmentCodesTableSettings.tsx'
import { isUserExistsInGroup } from '../../utils/userRights.ts'

const AssignmentCodes: React.FC = () => {
  const { subAccount } = useSubAccountDetails()
  const navigate = useNavigate()
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [changeStatus, setChangeStatus] = useState(false)
  const [editObject, setEditObject] = useState<UserCode | null>(null)

  const [refreshKey, setRefreshKey] = useState(0)
  const [surveyEnabled, setSurveyEnabled] = useState(false)
  const { state } = useAuth()

  const isMainUser = isUserExistsInGroup(state.groups, 'dd_panel_main_users')

  useEffect(() => {
    const isEnabled = subAccount?.surveys_enabled ?? isMainUser
    if (isEnabled) setSurveyEnabled(isEnabled)
  }, [subAccount, state.groups])

  useEffect(() => {
    changeStatus && setRefreshKey(refreshKey + 1)
    changeStatus && setChangeStatus(false)
  }, [changeStatus])

  const fetchData = async () => {
    return await userAssignmentCodes.getCodes()
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['assignmentCodes', refreshKey, state.email],
    queryFn: fetchData
  })

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  const mainCodeId = data && data.length > 0 ? data[data.length - 1].id : 0
  const columns = useMemo(
    (): any => setAssignmentCodesTableColumns(mainCodeId, surveyEnabled),
    [mainCodeId, surveyEnabled]
  )

  const actions = useMemo(
    () => setAssignmentCodesTableActions(navigate, mainCodeId),
    [isPopupVisible, surveyEnabled]
  )

  const onAddNewCode = () => {
    setIsPopupVisible(true)
    setEditObject(null)
  }

  const onClosePopup = () => {
    setIsPopupVisible(false)
    setEditObject(null)
  }

  const getRowClassNameForMainCode = (row: UserCode): string => {
    if (row.id === mainCodeId)
      return 'bg-blue-50 dark:bg-gray-900 dark:border-2'
    return ''
  }

  return (
    <>
      <div className="flex flex-1 flex-row justify-between items-center gap-4 mb-4">
        <div>
          <Breadcrumbs pageName="Kody przypisujące" breadcrumbs={[]} />
        </div>
        <div>
          <Button
            variant="filled"
            size="medium"
            color="primary"
            type="button"
            className="mb-3"
            onClick={onAddNewCode}
            icon={<AiOutlinePlus />}
          >
            {'Dodaj nowy kod'}
          </Button>
        </div>
      </div>

      <Card>
        <Card.Title>Lista kodów</Card.Title>
        <Card.Body>
          {isLoading && <Loader label={'Pobieranie danych...'} />}

          {data && !isLoading && (
            <Table
              columns={columns}
              data={data}
              actions={actions}
              rowClassName={getRowClassNameForMainCode}
            />
          )}

          {!isLoading && data && data.length === 0 && (
            <div className="m-12 mx-0">
              <Alert
                type="warning"
                message="Brak kodów identyfikujących."
                icon={<BiQrScan className="text-lg" />}
              />
            </div>
          )}
        </Card.Body>
      </Card>
      <Popup isVisible={isPopupVisible} onClose={onClosePopup} size="auto">
        <PopupAddCode
          editObject={editObject}
          setChangeStatus={setChangeStatus}
          setIsPopupVisible={setIsPopupVisible}
          mainCodeId={mainCodeId}
        />
      </Popup>
    </>
  )
}

export default AssignmentCodes
